<template>
  <div class="px-lg-4 px-3 d-flex flex-column claim-form" v-if="userPermissions.manageInsuranceClaims">
    <ValidationObserver ref="formValidationObserver" v-slot="{ invalid }">
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between p-0">
        <h3>Add/Edit Claims</h3>
        <div>
          <b-btn variant="primary" @click="doSaveClaim" :disabled="invalid">Save</b-btn>
          <router-link :to="{name: 'EventManage', params: {id: eventToManage.id, tab: 'claims'}}">
            <b-btn variant="secondary">Close</b-btn>
          </router-link>
        </div>
      </div>
    </div>
    <div class="overflow-hidden h-100">
      <b-form class="h-100">
        <div class="row no-gutters h-100 pb-3">
          <div class="col-xl-8 col-lg-8 col-md-12 col-12 p-0 form-container h-100 overflow-auto">
            <div>
              <b-tabs content-class="mt-3 p-3" card>
                <b-tab title="Details" active>
                  <div class="row">
                    <!--Claim-->
                    <div class="col-12">
                      <div class="d-flex align-items-center mb-4">
                        <span class="fa-stack">
                          <i class="fas fa-circle fa-stack-2x text-secondary"></i>
                          <i class="fas fa-list-ul fa-stack-1x fa-inverse"></i>
                        </span>
                        <h5 class="text-dark m-0 pl-2">Claim Details</h5>
                      </div>
                    </div>
                    <div class="col-lg-8 col-12">
                      <b-form-group>
                        <ValidationProvider name="ClaimTitle" rules="required" v-slot="validation" immediate>
                          <label class="control-label">Title</label>
                          <b-input placeholder="25 character description" v-model="claimForEdit.title" :state="spr2_getValidationState(validation)" />
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                    <div class="col-md-4 col-12">
                      <b-form-group>
                        <label class="control-label">Claim Status</label>
                        <b-form-select :options="[
                          { text: 'New', value: 0 },
                          { text: 'Under Review', value: 1 },
                          { text: 'In Litigation', value: 2 },
                          { text: 'Pending Claim Number', value: 3 },
                          { text: 'Pending Estimates', value: 4 },
                          { text: 'Pending Payment/Settled', value: 5 },
                          { text: 'Pending Repairs/Paid', value: 6 },
                          { text: 'Closed Denied', value: 7 },
                          { text: 'Closed Paid', value: 8 },
                          { text: 'Closed Resident Out Of Pocket', value: 9 },
                          { text: 'Closed Uncollected', value: 10 },
                        ]"
                          v-model="claimForEdit.claimStatus">
                        </b-form-select>
                      </b-form-group>
                    </div>
                    <div class="col-md-4 col-12">
                      <b-form-group>
                        <label class="control-label">Claim Type (policy holder)</label>
                        <b-form-select :options="[
                          { text: 'Property Liability',      value: 0 },
                          { text: 'Resident Liability',      value: 1 },
                          { text: 'Property Casualty',       value: 2 },
                          { text: 'Vendor Liability',        value: 3 },
                          { text: 'Business Interruption',   value: 4 },
                          { text: 'Loss of Rents',           value: 5 }
                        ]"
                          v-model="claimForEdit.claimType" @change="claimChanged(claimForEdit.claimType)">
                        </b-form-select>
                      </b-form-group>
                    </div>
                    <div class="col-md-4 col-12">
                      <b-form-group>
                        <label class="control-label">Cause of Loss</label>
                        <b-form-select :options="claimPerilTypeOptions" v-model="claimForEdit.claimPerilType">
                          <template #first>
                            <b-form-select-option :value="null">— Not Specified —</b-form-select-option>
                          </template>
                        </b-form-select>
                      </b-form-group>
                    </div>
                    <div class="col-md-4 col-12">
                      <b-form-group>
                        <ValidationProvider name="ClaimDate" rules="required" v-slot="validation" immediate>
                          <label class="control-label">Claim Date</label>
                          <span v-if="validationClaimDateError"><i class="fa fa-info-circle text-danger" :title="validationClaimDateError"></i></span>
                          <b-form-input class="form-control" type="date" v-model="claimForEdit.claimDate"
                          name="claimDate" id="claimDate" :state="spr2_getValidationState(validation) && validationClaimDateError == null" onfocus="blur()" onclick="this.showPicker()" @change="loadPolicyChoices"></b-form-input>
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                    <div class="col-lg-6 col-12">
                      <b-form-group>
                        <label>Reason for Claim</label>
                        <textarea class="form-control" rows="3" placeholder="Why was this claim made?"
                          v-model="claimForEdit.reason"></textarea>
                      </b-form-group>
                    </div>
                    <div class="col-lg-6 col-12">
                      <b-form-group>
                        <label>Determined Cause</label>
                        <textarea class="form-control" rows="3" placeholder="Official explanation of what happened"
                          v-model="claimForEdit.determinedCause"></textarea>
                      </b-form-group>
                    </div>
                    <!--Insurance-->
                    <div class="col-12 mt-5 mb-2 d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center w-100 mb-2">
                        <span class="fa-stack">
                          <i class="fas fa-circle fa-stack-2x text-secondary"></i>
                          <i class="fas fa-building fa-stack-1x fa-inverse"></i>
                        </span>
                        <h5 class="text-dark m-0 pl-2">{{ isTrackedPolicyClaimType(claimForEdit.claimType) ? 'Policies' : 'Untracked Policy' }}</h5>
                      </div>
                      <div class="d-flex justify-content-end align-items-center w-100">
                        <div class="mr-2" v-if="isTrackedPolicyClaimType(claimForEdit.claimType)">
                          <ValidationProvider name="PolicySelection" :rules="{ required: true, policyRequired: true }" v-slot="validation" immediate>
                            <input type="hidden" :value="selectedPolicyCount" />
                            <span v-if="validation.errors[0]" class="text-danger small d-block mb-2">
                              <i class="fa fa-exclamation-circle"></i> {{ validation.errors[0] }}
                            </span>
                          </ValidationProvider>
                        </div>
                        <b-btn v-if="isTrackedPolicyClaimType(claimForEdit.claimType) && !selectedContainerId" variant="success" size="sm" @click="addPolicy">Add <i class="fa fa-plus-circle"></i></b-btn>
                      </div>
                    </div>
                    <div class="col-12">
                      <!-- Policy Container picker -->
                      <div v-if="claimForEdit.claimType === 0 || claimForEdit.claimType === 2" class="mb-3">
                        <b-form-group>
                          <label class="control-label">Policy Container (optional)</label>
                          <b-form-select
                            v-model="selectedContainerId"
                            :options="containerChoices"
                            value-field="id"
                            text-field="name"
                            :disabled="containerChoices.length === 0"
                            @input="claimForEdit.containerId = selectedContainerId"
                          >
                            <template #first>
                              <b-form-select-option :value="null">
                                {{ containerChoices.length === 0 ? 'Set a claim date to load containers' : 'None (use individual policies)' }}
                              </b-form-select-option>
                            </template>
                          </b-form-select>
                        </b-form-group>
                      </div>
                      <!--Tracked Policies-->
                      <div v-if="isTrackedPolicyClaimType(claimForEdit.claimType) && !selectedContainerId">
                        <div class="card rounded mb-3" v-for="(policy, index) in claimForEdit.selectedPolicies" :key="getPolicyId(policy) ? getPolicyId(policy) : 'temp-' + index">
                          <div class="card-body">
                            <div class="row">
                              <div class="col-lg-7 col-12">
                                <b-form-group>
                                  <div class="d-flex justify-content-between align-items-center">
                                    <span><label>Policy</label> <span class="text-muted small">({{ getAvailablePolicies(index).length }} available)</span></span>
                                  </div>
                                  <ValidationProvider :name="'Policy ' + (index + 1)" rules="required" v-slot="validation" immediate>
                                    <b-form-select :options="getAvailablePolicies(index)"
                                      v-model="claimForEdit.selectedPolicies[index].id"
                                      value-field="id"
                                      text-field="name"
                                      placeholder="Select a policy"
                                      @input="onPolicySelected($event, index)"
                                      :disabled="isPolicyDropdownDisabled"
                                      :state="spr2_getValidationState(validation)"
                                      >
                                      <template #first>
                                        <b-form-select-option :value="null">
                                        {{ claimForEdit.policyChoices.length > 0 ? 'Select a policy' : 'Select a valid claim date' }} 
                                        </b-form-select-option>
                                      </template>
                                    </b-form-select>
                                  </ValidationProvider>
                                </b-form-group>  
                              </div>
                              <div class="col-lg-5 col-6">
                                <b-form-group>
                                  <label>Carrier Name</label>
                                  <b-input class="form-control" v-model="policy.carrierName" :disabled="true" />
                                </b-form-group>
                              </div>
                              <div class="col-lg-4 col-6">
                                <b-form-group>
                                  <label>Policy Number</label>
                                  <b-input class="form-control" v-model="policy.policyNumber" :disabled="true" />
                                </b-form-group>
                              </div>
                              <div class="col-md-4 col-6">
                                <b-form-group>
                                  <label>Deductible Paid</label>
                                  <currency-input v-model="policy.deductiblePaid" />
                                </b-form-group>
                              </div>
                              <div class="col-md-4 col-6">
                                <b-form-group>
                                  <label>Amount Received</label>
                                  <currency-input v-model="policy.amountReceived" />
                                </b-form-group>
                              </div>
                            </div>
                            <div class="row">
                              <div class="col-6">
                                <i @click="removePolicy(index)" class="fa fa-trash text-danger" style="cursor: pointer"></i>
                              </div>
                              <div class="col-6">
                                <small class="d-flex justify-content-end">
                                  <a href="#" @click.prevent="toggleTrackedAdjusterFields(index)" class="text-primary text-small">
                                    <!-- <i :class="showTrackedAdjusterFields[index] ? 'fa fa-eye-slash' : 'fa fa-eye'"></i> -->
                                    {{ showTrackedAdjusterFields[index] ? 'Hide' : 'View' }} Adjuster
                                  </a>
                                </small>
                              </div>
                            </div>
                            <div v-show="showTrackedAdjusterFields[index]" class="bg-muted border p-3 rounded">
                              <strong>{{ policy.adjusterName }}</strong>
                              <div class="d-flex">
                                <span class="mr-2">{{ policy.adjusterPhoneNumber }}</span>&bull;
                                <span class="mx-2">{{ policy.adjusterEmail }}</span>
                              </div>
                            </div>
                            <b-alert v-if="getApplicableDeductible(index)" show variant="info" class="mt-2 mb-0 py-2">
                              <i class="fa fa-info-circle mr-1"></i>
                              <strong>Deductible hint:</strong>
                              <span v-if="getApplicableDeductible(index).isSpecial">
                                {{ getApplicableDeductible(index).typeName }} deductible applies —
                                <span v-if="getApplicableDeductible(index).isPercentage">
                                  {{ getApplicableDeductible(index).amount }}%
                                  <span v-if="getApplicableDeductible(index).minAmount"> (min {{ getApplicableDeductible(index).minAmount | formatCurrency }})</span>
                                  <span v-if="getApplicableDeductible(index).maxAmount"> (max {{ getApplicableDeductible(index).maxAmount | formatCurrency }})</span>
                                </span>
                                <span v-else>{{ getApplicableDeductible(index).amount | formatCurrency }}</span>
                              </span>
                              <span v-else>
                                Standard (AOP) deductible applies —
                                {{ getApplicableDeductible(index).amount | formatCurrency }}
                                <span v-if="policy.standardDeductibleIncluded"> (included in coverage)</span>
                              </span>
                            </b-alert>
                            <!-- <b-form-group>
                              <label>Claim Number</label>
                              <b-input class="form-control" placeholder="Number" v-model="claimForEdit.claimNumber" />
                            </b-form-group> -->
                          </div>
                        </div>
                      </div>
                      <!--Untracked Policy-->
                      <div v-else-if="!isTrackedPolicyClaimType(claimForEdit.claimType)">
                        <ValidationProvider name="UntrackedPolicy" :rules="{ required: true, untrackedPolicyRequired: true }" v-slot="validation" immediate>
                          <input type="hidden" :value="hasUntrackedPolicyData" />
                          <span v-if="validation.errors[0]" class="text-danger small d-block mb-2">
                            <i class="fa fa-exclamation-circle"></i> {{ validation.errors[0] }}
                          </span>
                        </ValidationProvider>
                        <div class="border p-3 rounded-lg bg-white mb-3">
                          <label class="d-block mb-3 font-weight-bold">This claim type uses an untracked policy.</label>
                          <b-form-group>
                            <label>Carrier Name</label>
                            <ValidationProvider name="Name" rules="required" v-slot="validation" immediate>
                              <b-input class="form-control" placeholder="Carrier Name" v-model="claimForEdit.untrackedPolicy.carrierName" :state="spr2_getValidationState(validation)" />
                            </ValidationProvider>
                          </b-form-group>                    
                          <b-form-group>
                            <label>Policy Number</label>
                            <ValidationProvider name="Name" rules="required" v-slot="validation" immediate>
                              <b-input class="form-control" placeholder="Policy #" v-model="claimForEdit.untrackedPolicy.policyNumber" :state="spr2_getValidationState(validation)" />
                            </ValidationProvider>
                          </b-form-group>
                          <b-form-group>
                            <label>Deductible Paid</label>
                            <currency-input v-model="claimForEdit.untrackedPolicy.deductiblePaid" />
                          </b-form-group>
                          <b-form-group>
                            <label>Amount Received</label>
                            <currency-input v-model="claimForEdit.untrackedPolicy.amountReceived" />
                          </b-form-group>
                          <small class="d-flex justify-content-end">
                            <a href="#" @click.prevent="showUntrackedAdjusterFields = !showUntrackedAdjusterFields" class="text-primary">
                              <!-- <i :class="showUntrackedAdjusterFields ? 'fa fa-eye-slash' : 'fa fa-eye'"></i> -->
                              {{ showUntrackedAdjusterFields ? 'Hide' : 'View' }} Adjuster
                            </a>
                          </small>
                          <div v-show="showUntrackedAdjusterFields">
                            <b-form-group>
                              <label>Adjuster Name</label>
                              <b-input class="form-control" placeholder="Name" v-model="claimForEdit.untrackedPolicy.adjusterName" />
                            </b-form-group>
                            <b-form-group>
                              <label>Adjuster Phone Number</label>
                              <b-input class="form-control" placeholder="Phone Number" v-model="claimForEdit.untrackedPolicy.adjusterPhoneNumber" />
                            </b-form-group>
                            <b-form-group>
                              <label>Adjuster Email</label>
                              <b-input class="form-control" placeholder="Email" v-model="claimForEdit.untrackedPolicy.adjusterEmail" />
                             </b-form-group>
                          </div>
                        </div>
                      </div>
                      <!-- Layer Allocation section -->
                      <div v-if="selectedContainerId && claimForEdit.layerAllocations && claimForEdit.layerAllocations.length > 0" class="mt-4">
                        <h6 class="font-weight-bold">Layer Allocation</h6>
                        <b-table
                          :items="claimForEdit.layerAllocations"
                          :fields="[
                            { key: 'layerName', label: 'Layer' },
                            { key: 'carrierName', label: 'Carrier' },
                            { key: 'participationPercent', label: 'Share', formatter: (v) => v != null ? (v * 100).toFixed(1) + '%' : '' },
                            { key: 'allocatedAmount', label: 'Amount', formatter: (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 0 }) : '' }
                          ]"
                          small
                          striped
                          responsive
                        ></b-table>
                      </div>
                    </div>
                  </div>
                </b-tab>
                <b-tab title="Notes" class="h-100">
                  <div class="col-12 h-100">
                    <b-form-group>
                      <label>Notes</label>
                      <textarea class="form-control notes-field h-100" placeholder="Enter notes..." v-model="claimForEdit.notes"></textarea>
                    </b-form-group>
                  </div>
                </b-tab>
              </b-tabs>
            </div>
          </div>
          <!--Costs-->
          <div class="col-xl-4 col-lg-4 col-md-12 col-12 px-xl-5 p-lg-4 p-0 h-100 overflow-auto">
            <div class="row mb-2 mt-lg-0 mt-4">
              <div class="col-12 d-flex align-items-center">
                <h5 class="text-dark m-0">Costs</h5>
              </div>
            </div>

            <!-- SECTION 1: INCURRED LOSS -->
            <div class="mb-4 border bg-light rounded p-3">
              <div class="border-left-3 border-danger pl-3">
                <h6 class="text-uppercase font-weight-bold mb-3">
                  Incurred Loss
                </h6>
                <div v-for="(expense, index) in claimForEdit.claimCosts" :key="index" class="d-flex align-items-center justify-content-between mb-2">
                  <label>{{ expense.categoryString }}</label>
                  <b-form-group class="mb-0">
                    <currency-input v-model="expense.amount" disabled />
                  </b-form-group>
                </div>
                <hr class="my-2" />
                <div class="d-flex align-items-center justify-content-between">
                  <label class="mb-0"><strong>Total Incurred Loss (Gross Loss)</strong></label>
                  <strong class="text-dark">{{ totalIncurredLoss | formatCurrency }}</strong>
                </div>
              </div>
            </div>

            <!-- SECTION 2: COVERAGE EXCLUSIONS -->
            <div class="mb-4 border bg-light rounded p-3">
              <div class="border-left-3 border-primary pl-3">
                <h6 class="text-uppercase font-weight-bold mb-3">
                  Coverage Exclusions
                </h6>
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <label>Uninsured / Excluded Expenses</label>
                  <span class="text-danger">({{ coverageExclusionsTotal | formatCurrency }})</span>
                </div>
                <hr class="my-2" />
                <div class="d-flex align-items-center justify-content-between">
                  <label class="mb-0"><strong>Covered Incurred Loss</strong></label>
                  <strong class="text-dark">{{ coveredIncurredLoss | formatCurrency }}</strong>
                </div>
              </div>
            </div>

            <!-- SECTION 3: INSURED RETENTIONS -->
            <div class="mb-4 border bg-light rounded p-3">
              <div class="border-left-3 border-warning pl-3">
                <h6 class="text-uppercase font-weight-bold mb-3">
                  Insured Retentions
                </h6>
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <label>Deductible Paid</label>
                  <span class="text-danger">({{ computedDeductiblePaid | formatCurrency }})</span>
                </div>
                <hr class="my-2" />
                <div class="d-flex align-items-center justify-content-between">
                  <label class="mb-0"><strong>Insured Retained Loss</strong></label>
                  <strong class="text-dark">{{ insuredRetainedLoss | formatCurrency }}</strong>
                </div>
              </div>
            </div>

            <!-- SECTION 4: INSURANCE RECOVERY -->
            <div class="mb-4 border bg-light rounded p-3">
              <div class="border-left-3 border-success pl-3">
                <h6 class="text-uppercase font-weight-bold mb-3">
                  Insurance Recovery
                </h6>
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <label>Net Loss Recoverable from Insurance</label>
                  <span class="text-success">{{ netLossRecoverableFromInsurance | formatCurrency }}</span>
                </div>
                <hr class="my-2" />
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <label class="mb-0"><strong>Total Paid by Insurance</strong></label>
                  <strong class="text-success">{{ totalPaidByInsurance | formatCurrency }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </div>
    </ValidationObserver>
  </div>

</template>

<script>
import { spr2Validation } from '@/mixins/Spr2SharedFunctions'
import { mapActions } from 'vuex'
import CurrencyInput from '@/components/CurrencyInput'
import { extend } from 'vee-validate'

// Custom validation rule for policy selection
extend('policyRequired', {
  validate: (value) => {
    return value > 0
  },
  message: 'At least one policy must be selected'
})

// Custom validation rule for untracked policy
extend('untrackedPolicyRequired', {
  validate: (value) => {
    return value === 'true' || value === true
  },
  message: 'Untracked policy information is required'
})

export default {
  mixins: [spr2Validation],
  name: 'AddEditClaim',
  components:{
    CurrencyInput,
  },
  props: {
    claimId: {
      type: String,
      default: null
    },
    eventId: {
      type: String,
      default: null
    },
    propertyId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      validationErrors: [],
      showUntrackedAdjusterFields: false,
      showTrackedAdjusterFields: {},
      containerChoices: [],
      selectedContainerId: null,
      claimPerilTypeOptions: [
        { value: 1, text: 'Wind and Hail' },
        { value: 2, text: 'Named Storm' },
        { value: 3, text: 'Flood' },
        { value: 4, text: 'Earthquake' },
        { value: 5, text: 'Fire' },
        { value: 6, text: 'Water Damage' },
        { value: 7, text: 'Equipment Breakdown' },
        { value: 8, text: 'Terrorism' },
        { value: 9, text: 'All Other Perils' },
        { value: 10, text: 'Other' }
      ]
    }
  },
  watch: {
    claimForEdit: {
      handler () {
        this.syncPolicySelections()
      },
      immediate: true
    },
    'claimForEdit.selectedPolicies': {
      handler () {
        this.syncPolicySelections()
      },
      deep: true
    },
    // repairCost (newVal, oldVal) {
    //   this.claimForEdit.repairCost = this.cleanseData(newVal)
    //   this.updateCalculatedValues()
    // },
    // mitigationCost (newVal, oldVal) {
    //   this.claimForEdit.mitigationCost = this.cleanseData(newVal)
    //   this.updateCalculatedValues()
    // },
    // businessInterruptionCost (newVal, oldVal) {
    //   this.claimForEdit.businessInterruptionCost = this.cleanseData(newVal)
    //   this.updateCalculatedValues()
    // },
    // settlementAmount (newVal, oldVal) {
    //   this.claimForEdit.settlementAmount = this.cleanseData(newVal)
    //   this.updateCalculatedValues()
    // }
  },
  created () {
    if (!this.userPermissions.manageInsuranceClaims) {
      this.$router.push('/events');
    }

    if (this.eventId != null && this.claimId != null) {
      this.editClaim()
    } else {
      this.addClaim()
    }
  },
  computed: {
    userPermissions () {
      return this.$store.state.loggedInUserPermissions;
    },
    claimForEdit () {
      return this.$store.state.events.claimForEdit
    },
    eventToManage () {
      return this.$store.state.events.eventToManage
    },
    validationClaimDateError () {
      let error = this.validationErrors.find((ve) => ve.field === 'ClaimDate')
      return error == null ? null : error.message
    },

    isPolicyDropdownDisabled () {
      // Disable when there's no claim date or options
      return (this.claimForEdit == null || this.claimForEdit.claimDate == null || this.claimForEdit.policyChoices.length  === 0)
    },
    hasUntrackedPolicyData () {
      // Check if any untracked policy field has data
      if (!this.claimForEdit || !this.claimForEdit.untrackedPolicy) {
        return false
      }
      const policy = this.claimForEdit.untrackedPolicy
      const hasData = !!(
        this.claimForEdit.untrackedPolicyInfo ||
        policy.carrierName ||
        policy.policyNumber ||
        policy.adjusterName ||
        policy.adjusterPhoneNumber ||
        policy.adjusterEmail
      )
      return hasData
    },

    selectedPolicyCount () {
      // A selected container satisfies the policy requirement (mutually exclusive)
      if (this.selectedContainerId) return 1
      if (!this.claimForEdit || !Array.isArray(this.claimForEdit.selectedPolicies)) {
        return 0
      }
      return this.claimForEdit.selectedPolicies.filter(policy => this.policyHasSelection(policy)).length
    },

    computedDeductiblePaid () {
      let total = 0

      // Sum from tracked policies
      if (this.claimForEdit && Array.isArray(this.claimForEdit.claimPolicies)) {
        total += this.claimForEdit.claimPolicies.reduce(
          (sum, policy) => sum + (policy.deductiblePaid || 0),
          0
        )
      }

      // Add from untracked policy
      if (this.claimForEdit && this.claimForEdit.untrackedPolicy && this.claimForEdit.untrackedPolicy.deductiblePaid) {
        total += this.claimForEdit.untrackedPolicy.deductiblePaid
      }

      return total
    },

    // New calculated field from backend
    totalIncurredLoss () {
      // This field is calculated by the backend and returned in the API response
      // Display as $0.00 if null/undefined
      if (this.claimForEdit && this.claimForEdit.totalIncurredLoss != null) {
        return this.claimForEdit.totalIncurredLoss
      }
      return 0
    },

    coverageExclusionsTotal () {
      // Sum of UninsuredExpense costs - calculated by backend
      if (this.claimForEdit && this.claimForEdit.coverageExclusionsTotal != null) {
        return this.claimForEdit.coverageExclusionsTotal
      }
      return 0
    },

    coveredIncurredLoss () {
      // TotalIncurredLoss minus CoverageExclusionsTotal - calculated by backend
      if (this.claimForEdit && this.claimForEdit.coveredIncurredLoss != null) {
        return this.claimForEdit.coveredIncurredLoss
      }
      return 0
    },

    insuredRetainedLoss () {
      // Amount retained by insured (deductibles paid) - calculated by backend
      if (this.claimForEdit && this.claimForEdit.insuredRetainedLoss != null) {
        return this.claimForEdit.insuredRetainedLoss
      }
      return 0
    },

    netLossRecoverableFromInsurance () {
      // Expected insurance recovery amount (CoveredIncurredLoss - InsuredRetainedLoss) - calculated by backend
      if (this.claimForEdit && this.claimForEdit.netLossRecoverableFromInsurance != null) {
        return this.claimForEdit.netLossRecoverableFromInsurance
      }
      return 0
    },

    totalPaidByInsurance () {
      // Actual amount received from all insurance policies - calculated by backend
      if (this.claimForEdit && this.claimForEdit.totalPaidByInsurance != null) {
        return this.claimForEdit.totalPaidByInsurance
      }
      return 0
    }
  },
  methods: {
    ...mapActions('events', [
      'saveClaim',
      'loadClaimForEdit',
      'createNewClaim',
      'loadClaimPolicyChoices'
    ]),
    isTrackedPolicyClaimType (claimType) {
      return claimType === 0 || claimType === 2 || claimType === 4 || claimType === 5
    },
    toggleTrackedAdjusterFields(index) {
      this.$set(this.showTrackedAdjusterFields, index, !this.showTrackedAdjusterFields[index])
    },
    buildEmptyPolicy () {
      return {
        id: null,
        policyId: null,
        name: null,
        carrierName: null,
        policyNumber: null,
        adjusterName: null,
        adjusterPhoneNumber: null,
        adjusterEmail: null,
        deductiblePaid: null,
        amountReceived: null
      }
    },
    normalizeCurrencyField (value) {
      if (value === '' || value === null || value === undefined) {
        return null
      }
      const parsed = typeof value === 'string' ? parseFloat(value) : value
      return isNaN(parsed) ? null : parsed
    },
    getPolicyId (entity) {
      if (!entity) {
        return null
      }
      if (entity.policyId !== undefined && entity.policyId !== null && entity.policyId !== '') {
        return entity.policyId
      }
      if (entity.id !== undefined && entity.id !== null && entity.id !== '') {
        return entity.id
      }
      return null
    },
    normalizePolicyId (value) {
      if (value === null || value === undefined) {
        return null
      }
      return value.toString()
    },
    policyHasSelection (policy) {
      const id = this.getPolicyId(policy)
      return id !== null && id !== undefined && id !== ''
    },
    syncPolicySelections () {
      if (!this.claimForEdit) {
        return
      }

      const selectedPolicies = Array.isArray(this.claimForEdit.selectedPolicies)
        ? this.claimForEdit.selectedPolicies
        : []

      const normalizedPolicies = selectedPolicies
        .filter(policy => this.policyHasSelection(policy))
        .map(policy => {
          const policyId = this.getPolicyId(policy)
          const policyNumber = policy.policyNumber
          return {
            ...policy,
            id: policyId,
            policyId,
            policyNumber: policyNumber != null ? policyNumber : null,
            deductiblePaid: this.normalizeCurrencyField(policy.deductiblePaid),
            amountReceived: this.normalizeCurrencyField(policy.amountReceived)
          }
        })

      this.$set(this.claimForEdit, 'claimPolicies', normalizedPolicies)

      const policyIds = normalizedPolicies
        .map(policy => policy.policyId)
        .filter(id => id !== null && id !== undefined && id !== '')

      this.$set(this.claimForEdit, 'policyIds', policyIds)
      this.$set(this.claimForEdit, 'selectedPolicyIds', policyIds)
    },
    clearTrackedPolicies () {
      if (!this.claimForEdit) {
        return
      }
      this.$set(this.claimForEdit, 'policyNumber', null)
      this.$set(this.claimForEdit, 'policyId', null)
      this.$set(this.claimForEdit, 'selectedPolicy', null)
      this.$set(this.claimForEdit, 'selectedPolicies', [])
      this.$set(this.claimForEdit, 'claimPolicies', [])
      this.$set(this.claimForEdit, 'policyIds', [])
      this.$set(this.claimForEdit, 'selectedPolicyIds', [])
      this.syncPolicySelections()
    },
    addClaim () {
      this.createNewClaim().then(() => {
        this.syncPolicySelections()
      })
    },
    editClaim () {
      console.log('loading claim for', this.eventId, this.claimId)
      this.loadClaimForEdit({ eventId: this.eventId, claimId: this.claimId }).then(() => {
        this.syncPolicySelections()
        if (this.claimForEdit && this.claimForEdit.claimDate) {
          this.loadPolicyChoices()
        }
      })
    },
    async doSaveClaim () {
      this.claimForEdit.eventId = this.eventId
      this.claimForEdit.propertyId = this.propertyId
      // this.claimForEdit.totalLoss = auto calculated
      // this.claimForEdit.adjustment = auto calculated
          
      if (this.claimForEdit.claimType === 1 || this.claimForEdit.claimType === 3) {
        this.clearTrackedPolicies()
      } else if (this.isTrackedPolicyClaimType(this.claimForEdit.claimType)) {
        this.claimForEdit.untrackedPolicyInfo = null
        this.$set(this.claimForEdit, 'untrackedPolicy', null)
      }

      this.syncPolicySelections()
      console.log(
        'Calling saveClaim with: ' +
          JSON.stringify(this.claimForEdit, null, 2)
      )
      let result = await this.saveClaim({ eventId: this.eventId, claim: this.claimForEdit })
      if(result.success){
        this.$toasted.success(result.message, { duration: 4000 })
        this.$router.push({ name: 'EventManage', params: { id: this.eventId || (this.eventToManage && this.eventToManage.id), tab: 'claims' } })
      } else {
        this.$toasted.error(result.message, { duration: 4000 })
        this.validationErrors = result.validationErrors
      }
    },
    claimChanged (claimType) {
      if ((claimType === 1 || claimType === 3)) {
        if (this.claimForEdit.untrackedPolicy == null) {
          this.$set(this.claimForEdit, 'untrackedPolicy', {
            carrierName: '',
            policyNumber: null,
            adjusterName: null,
            adjusterPhoneNumber: null,
            adjusterEmail: null,
            deductiblePaid: null,
            amountReceived: null
          })
        }
        this.clearTrackedPolicies()
        console.log('Clearing selected policies for claim type', claimType)
      } else {
        this.syncPolicySelections()
      }
      //this.updateInsuranceInfo()
    },
    updateInsuranceInfo () {
      if (
        this.property != null &&
        Array.isArray(this.property.insurancePolicies) &&
        this.property.insurancePolicies.length > 0 &&
        Array.isArray(this.claimForEdit.selectedPolicies) &&
        this.claimForEdit.selectedPolicies.length > 0
      ) {
        const sourcePolicy = this.property.insurancePolicies[0]
        const targetPolicy = this.claimForEdit.selectedPolicies[0]
        targetPolicy.policyNumber = sourcePolicy.policyNumber
        targetPolicy.contactNumber = sourcePolicy.contactNumber
        targetPolicy.deductiblePaid = sourcePolicy.deductiblePaid
      }
    },
    cleanseData (data) {
      if (data) {
        let newVal = parseFloat(data.toString().replaceAll('$', '').replaceAll(',', '') || 0)
        return newVal
      } else {
        return 0
      }
    },
    updateCalculatedValues () {
      let ctl = (this.claimForEdit.repairCost || 0) + (this.claimForEdit.mitigationCost || 0) + (this.claimForEdit.businessInterruptionCost || 0) + (this.claimForEdit.damageRelatedExpenseTotal || 0)
      let ca = ctl - (this.claimForEdit.settlementAmount || 0)
      this.claimForEdit.totalLoss = ctl
      this.claimForEdit.adjustment = ca
    },
    onPolicySelected (selectedId, index) {
      if (!Array.isArray(this.claimForEdit.selectedPolicies)) {
        this.$set(this.claimForEdit, 'selectedPolicies', [])
      }

      if (!selectedId) {
        this.$set(this.claimForEdit.selectedPolicies, index, this.buildEmptyPolicy())
        this.syncPolicySelections()
        return
      }

      const normalizedSelectedId = this.normalizePolicyId(selectedId)
      const policyChoice = Array.isArray(this.claimForEdit.policyChoices)
        ? this.claimForEdit.policyChoices.find(p => this.normalizePolicyId(this.getPolicyId(p)) === normalizedSelectedId)
        : null

      if (!policyChoice) {
        this.syncPolicySelections()
        return
      }

      const previousPolicy = this.claimForEdit.selectedPolicies[index] || this.buildEmptyPolicy()
      const policyId = this.getPolicyId(policyChoice)
      const policyNumber = policyChoice.policyNumber != null ? policyChoice.policyNumber : policyChoice.number
      const deductiblePaid = policyChoice.deductiblePaid != null
        ? policyChoice.deductiblePaid
        : previousPolicy.deductiblePaid
      const amountReceived = policyChoice.amountReceived != null
        ? policyChoice.amountReceived
        : previousPolicy.amountReceived

      this.$set(this.claimForEdit.selectedPolicies, index, {
        id: policyId,
        policyId,
        name: policyChoice.name != null ? policyChoice.name : previousPolicy.name,
        carrierName: policyChoice.carrierName != null ? policyChoice.carrierName : previousPolicy.carrierName,
        policyNumber,
        adjusterName: policyChoice.adjusterName != null ? policyChoice.adjusterName : previousPolicy.adjusterName,
        adjusterPhoneNumber: policyChoice.adjusterPhoneNumber != null ? policyChoice.adjusterPhoneNumber : previousPolicy.adjusterPhoneNumber,
        adjusterEmail: policyChoice.adjusterEmail != null ? policyChoice.adjusterEmail : previousPolicy.adjusterEmail,
        deductiblePaid,
        amountReceived,
        standardDeductible: policyChoice.standardDeductible || 0,
        standardDeductibleIncluded: policyChoice.standardDeductibleIncluded || false,
        specialDeductibles: policyChoice.specialDeductibles || []
      })

      this.syncPolicySelections()

      console.log('Updated selectedPolicies:', this.claimForEdit.selectedPolicies)
    },
    /**
     * Returns a hint descriptor for which deductible applies to this policy slot
     * based on the selected cause of loss (claimPerilType).
     *
     * Returns null when:
     *   - no policy is selected for this slot
     *   - the standard deductible is $0 and there are no special deductibles
     *
     * Returns an object:
     *   { isSpecial: false, amount, ... }  for the AOP/Standard deductible
     *   { isSpecial: true, typeName, amount, isPercentage, minAmount, maxAmount }  for a peril match
     */
    getApplicableDeductible (index) {
      const policy = this.claimForEdit.selectedPolicies && this.claimForEdit.selectedPolicies[index]
      if (!policy || !policy.id) return null

      const peril = this.claimForEdit.claimPerilType  // numeric or null

      // Peril-to-keyword mapping (mirrors InsuranceClaimService.ResolveApplicableDeductible)
      const perilKeywords = {
        1: ['wind', 'hail'],        // WindAndHail
        2: ['named', 'storm', 'hurricane'], // NamedStorm
        3: ['flood'],               // Flood
        4: ['earthquake', 'seismic', 'earth'], // Earthquake
        5: ['fire'],                // Fire
        6: ['water'],               // WaterDamage
        7: ['equipment', 'breakdown'], // EquipmentBreakdown
        8: ['terror']               // Terrorism
      }

      const keywords = peril != null ? (perilKeywords[peril] || []) : []

      if (keywords.length > 0 && Array.isArray(policy.specialDeductibles)) {
        const match = policy.specialDeductibles.find(sd =>
          sd.type && sd.type.name &&
          keywords.some(kw => sd.type.name.toLowerCase().includes(kw))
        )
        if (match) {
          return {
            isSpecial: true,
            typeName: match.type.name,
            amount: match.amount,
            isPercentage: match.isPercentage,
            minAmount: match.minimumAmount || null,
            maxAmount: match.maximumAmount || null
          }
        }
      }

      // Fall back to standard deductible — only show when it has a non-zero value
      if (policy.standardDeductible) {
        return {
          isSpecial: false,
          amount: policy.standardDeductible
        }
      }

      return null
    },
    addPolicy () {
      console.log('Adding a new policy', this.claimForEdit)
      if (!Array.isArray(this.claimForEdit.selectedPolicies)) {
        this.$set(this.claimForEdit, 'selectedPolicies', [])
      }
      this.claimForEdit.selectedPolicies.push(this.buildEmptyPolicy())
      this.syncPolicySelections()
    },
    removePolicy (index) {
      if (Array.isArray(this.claimForEdit.selectedPolicies) && index >= 0 && index < this.claimForEdit.selectedPolicies.length) {
        this.claimForEdit.selectedPolicies.splice(index, 1)
        this.syncPolicySelections()
      }
    },  
    async loadPolicyChoices () {
      if (this.claimForEdit && this.claimForEdit.claimDate) {
        const args = { eventId: this.eventId, claimDate: this.claimForEdit.claimDate }
        await this.loadClaimPolicyChoices(args)
        this.containerChoices = this.claimForEdit.containerChoices || []
        this.selectedContainerId = this.claimForEdit.containerId || null
      } else {
        this.claimForEdit.policyChoices = []
        if (Array.isArray(this.claimForEdit.selectedPolicies)) {
          this.claimForEdit.selectedPolicies.forEach((policy, idx) => {
            const resetPolicy = this.buildEmptyPolicy()
            Object.keys(resetPolicy).forEach(key => {
              this.$set(this.claimForEdit.selectedPolicies[idx], key, resetPolicy[key])
            })
          })
        }
        this.syncPolicySelections()
      }
    },
    getAvailablePolicies (index) {
      if (!Array.isArray(this.claimForEdit.policyChoices)) {
        return []
      }

      const selectedPolicyIds = Array.isArray(this.claimForEdit.selectedPolicies)
        ? this.claimForEdit.selectedPolicies
            .filter((sp, i) => i !== index)
            .map(p => this.normalizePolicyId(this.getPolicyId(p)))
            .filter(id => id !== null)
        : []

      return this.claimForEdit.policyChoices.filter(pc => {
        const optionId = this.normalizePolicyId(this.getPolicyId(pc))
        if (optionId === null) {
          return true
        }
        return !selectedPolicyIds.includes(optionId)
      })
    }
  }
}
</script>

<style scoped>
   /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  } 
  /* Firefox */
  input[type=number] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .claim-form {
    height: calc(100vh - 72px);
  }

  @media screen and (max-width: 1199px) {
    .claim-form {
      height: 100%;
    }
  }

  .claim-form > span {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;
  }

  .border-left-3 {
    border-left-style: solid;
    border-left-width: 3px;
  }

  .notes-field {
    height: calc(100vh - 300px) !important;
    resize: none;
  }
</style>
